<script lang="ts" setup>
import { mdiLightningBolt, mdiLock } from "@mdi/js";
import { useBetbook } from "~/store/betbook";
import { useBetslip } from "~/store/betslip";
import { IEvent } from "~/types/sports/event";
import { IMarket } from "~/types/sports/market";
import { IOutcome } from "~/types/sports/outcome";
import { IPrice } from "~/types/sports/price";

interface IProps {
	event: IEvent;
	market: IMarket | undefined;
	outcome: IOutcome | undefined;
	prices: IPrice[];
	pricesMap: Map<string, IPrice>;
}

const props = defineProps<IProps>();

const betslip = useBetslip();
const betbook = useBetbook();

const outcomePrice = computed(() => props.pricesMap.get(props.outcome!.outcomeId));
const outcomeSelected = computed(() => {
	return !!betslip.activeSelections.find((selection) => selection.outcomeId === props.outcome?.outcomeId);
});
const isDisabled = computed(
	() =>
		!props.outcome?.isTradingActive ||
		!props.outcome?.shouldDisplay ||
		props.market?.isSuspended ||
		!betbook.isEventActive(props.event) ||
		outcomePrice.value?.priceDecimal === 1
);

const handleSelection = () => {
	if (isDisabled.value) return;
	betslip.handleSelection({ event: props.event, market: props.market, outcome: props.outcome, price: outcomePrice.value });
};
</script>

<template>
	<button
		@click="handleSelection"
		class="flex items-center text-dark-800 dark:text-light-50 justify-center w-full h-full rounded text-xs p-1 border border-solid border-light-400 dark:border-dark-400 transition-all"
		:class="{ 'bg-identity text-light-50 border-primary-600 dark:border-primary-600': outcomeSelected }"
	>
		<div v-if="isDisabled" class="flex items-center justify-center w-full min-h-[24px]">
			<UiIcon class="w-4 h-4 dark:fill-light-500" :path="mdiLock" />
		</div>
		<div v-else class="flex items-center justify-between w-full">
			<span>{{ outcome?.displayName }}</span>
			<div class="flex items-center justify-center">
				<UiIcon class="w-4 h-4 fill-warning-600" :path="mdiLightningBolt" />
				<!-- TODO: Add price increase or decrease indicators -->
				<strong>{{ formatOdds(outcomePrice!.priceDecimal, outcomePrice!.denominator, outcomePrice!.numerator) }}</strong>
			</div>
		</div>
	</button>
</template>
